/**
 * Einsicht in das Änderungsprotokoll.
 */

import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { formatTimestamp } from '@erp/shared';

import { api, withQuery } from '../api/client';
import type { AuditEntry } from '../api/types';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyState, LoadingBlock, TextInput } from '../components/ui';

const ACTION_LABELS: Record<string, string> = {
  create: 'Angelegt',
  update: 'Geändert',
  status_change: 'Status geändert',
  delete: 'Gelöscht',
  post: 'Festgeschrieben',
  cancel: 'Storniert',
  login: 'Anmeldung',
  login_failed: 'Anmeldung fehlgeschlagen',
  logout: 'Abmeldung',
  export: 'Export',
  print: 'Druck',
  email: 'E-Mail',
  settings_change: 'Einstellung geändert',
  password_change: 'Passwort geändert',
  lock: 'Gesperrt',
  unlock: 'Entsperrt',
};

const ENTITY_LABELS: Record<string, string> = {
  partner: 'Geschäftspartner',
  item: 'Artikel',
  user: 'Benutzer',
  company: 'Firmendaten',
  number_range: 'Nummernkreis',
  payment_term: 'Zahlungsbedingung',
  document: 'Beleg',
  payment: 'Zahlung',
  document_template: 'Belegvorlage',
  complaint: 'Reklamation',
  repair: 'Reparatur',
};

function badgeKind(action: string): 'neutral' | 'success' | 'warning' | 'danger' | 'accent' {
  if (action === 'create') return 'success';
  if (action === 'delete' || action === 'cancel' || action === 'login_failed') return 'danger';
  if (action === 'post') return 'accent';
  if (action === 'settings_change' || action === 'password_change') return 'warning';
  return 'neutral';
}

export function AuditPage() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 100;

  const { data, isLoading } = useQuery({
    queryKey: ['audit', search, action, from, to, offset],
    queryFn: () =>
      api.get<{ entries: AuditEntry[]; total: number }>(
        withQuery('/api/audit', {
          q: search || undefined,
          action: action || undefined,
          from: from || undefined,
          to: to || undefined,
          limit,
          offset,
        }),
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Änderungsprotokoll"
        subtitle="Wer hat wann was geändert. Einträge können weder geändert noch gelöscht werden."
      />

      <div className="card">
        <div className="table-toolbar">
          <TextInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setOffset(0);
            }}
            placeholder="Suche nach Datensatz oder Benutzer …"
          />
          <select
            className="select input"
            value={action}
            style={{ width: 220 }}
            onChange={(event) => {
              setAction(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">Alle Vorgänge</option>
            {Object.entries(ACTION_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          {/* Zeitraum: Am Jahresende zum Januar zu scrollen ist keine Option. */}
          <div className="row" style={{ gap: 6, alignItems: 'center' }}>
            <span className="faint">von</span>
            <div style={{ width: 150 }}>
              <TextInput
                type="date"
                value={from}
                onChange={(value) => {
                  setFrom(value);
                  setOffset(0);
                }}
              />
            </div>
            <span className="faint">bis</span>
            <div style={{ width: 150 }}>
              <TextInput
                type="date"
                value={to}
                onChange={(value) => {
                  setTo(value);
                  setOffset(0);
                }}
              />
            </div>
            {from || to ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setFrom('');
                  setTo('');
                  setOffset(0);
                }}
              >
                Zeitraum löschen
              </button>
            ) : null}
          </div>
        </div>

        {isLoading ? <LoadingBlock /> : null}

        {data && !isLoading ? (
          data.entries.length === 0 ? (
            <EmptyState title="Keine Einträge" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Zeitpunkt</th>
                    <th className="shrink">Benutzer</th>
                    <th className="shrink">Vorgang</th>
                    <th className="shrink">Art</th>
                    <th>Datensatz</th>
                    <th>Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                        {formatTimestamp(entry.timestamp)}
                      </td>
                      <td className="mono">{entry.userName}</td>
                      <td>
                        <Badge kind={badgeKind(entry.action)}>
                          {ACTION_LABELS[entry.action] ?? entry.action}
                        </Badge>
                      </td>
                      <td className="faint">{ENTITY_LABELS[entry.entityType] ?? entry.entityType}</td>
                      <td>{entry.entityLabel ?? ''}</td>
                      <td>
                        <ChangeSummary entry={entry} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}

        {data && data.total > limit ? (
          <div className="table-footer">
            <span>
              {offset + 1} – {Math.min(offset + limit, data.total)} von {data.total}
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset + limit >= data.total}
                onClick={() => setOffset(offset + limit)}
              >
                Weiter
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Zeigt geaenderte Felder als "Feld: alt → neu". */
function ChangeSummary({ entry }: { entry: AuditEntry }) {
  if (entry.note) return <span className="muted">{entry.note}</span>;
  if (!entry.changes) return null;

  const entries = Object.entries(entry.changes);
  const isDiff = entries.every(
    ([, value]) => value !== null && typeof value === 'object' && 'von' in (value as object),
  );

  if (!isDiff) {
    return <span className="faint">{entries.length} Felder</span>;
  }

  return (
    <div style={{ fontSize: 12.5 }}>
      {entries.slice(0, 4).map(([field, value]) => {
        const change = value as { von: unknown; nach: unknown };
        return (
          <div key={field}>
            <span className="faint">{field}: </span>
            <span className="muted" style={{ textDecoration: 'line-through' }}>
              {display(change.von)}
            </span>{' '}
            → <span>{display(change.nach)}</span>
          </div>
        );
      })}
      {entries.length > 4 ? <div className="faint">… und {entries.length - 4} weitere</div> : null}
    </div>
  );
}

function display(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(leer)';
  if (value === true) return 'ja';
  if (value === false) return 'nein';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
